import { MouseEvent as ReactMouseEvent } from 'react';
import { ThreeEvent } from '@react-three/fiber';

import { getSelectedItemIdentifiers } from '../../library/selection/selectors';
import { isSelectableItemType, SelectedItemIdentifier } from '../../library/selection/types';
import { threeJsDom } from '../../three/util/events/ThreeJSDom';
import { GlobalThreeJSDomObjectEvent } from '../../three/util/events/types';
import { getCurrentlyRightClickableItemTypes } from '../selectors/getCurrentlyClickableItemTypes';
import { getSelectedEntryFactory } from '../selectors/getSelectedItemSelector';
import {
  RightClickedItemPosition,
  RightClickedItemWithoutPosition,
  setRightClickedItem,
} from '../slices/viewportSlice';
import { selectEntry } from './selectActions';
import { AsyncThunkActionReturn } from './thunkActions';

interface ContextMenuRequestOptions {
  // If true, indicates that the action prompting the context menu happened outside the scene. Defaults to false.
  readonly outsideScene?: boolean;

  // If set, the menu replacement identified by this string will be shown if possible.
  readonly withReplacement?: string;
}

// Items that are immune from the default "select on context menu" behavior:
//   component designs    -- so the left hand sidebar can open a menu without changing the active component
//   projects             -- because we want to avoid switching into layout mode when just opening a context menu
//   reference lines      -- because the context menu is how multiple lines are disambiguated during selection
//   buildings and levels -- because selecting them could alter the layout hierarchy filtering
const ITEMS_NOT_SELECTED_WITH_CONTEXT_MENU = new Set([
  'componentDesign',
  'project',
  'referenceLine',
  'building',
  'level',
]);

/** Returns a boolean indicating whether a menu was actually opened. */
export const requestContextMenu =
  (
    event: GlobalThreeJSDomObjectEvent | MouseEvent | ReactMouseEvent | TouchEvent | ThreeEvent<MouseEvent>,
    item: RightClickedItemWithoutPosition,
    options?: ContextMenuRequestOptions,
  ): AsyncThunkActionReturn<boolean> =>
  async (dispatch, getState) => {
    const state = getState();

    if (item.type && !getCurrentlyRightClickableItemTypes(state).has(item.type)) return false;

    if (item?.type && isSelectableItemType(item.type) && !ITEMS_NOT_SELECTED_WITH_CONTEXT_MENU.has(item.type)) {
      if (item.type === 'referenceLine') throw new Error(); // TODO: typescript

      const entry = getSelectedEntryFactory(state)(item as SelectedItemIdentifier); // TODO: typescript
      if (entry == null) throw new Error('Right clicked item could not be found in state.');

      const alreadySelected = getSelectedItemIdentifiers(state).some(
        (item) => item.type === entry.type && item.guid === entry.item.guid,
      );

      await dispatch(selectEntry(entry, { exclusive: !alreadySelected }));
    }

    let position: RightClickedItemPosition =
      'eventPosition' in event ? event.eventPosition : threeJsDom.getEventPosition(event);

    if (options?.outsideScene) {
      position = { ...position, outsideScene: true };
    }

    dispatch(setRightClickedItem({ ...item, position, requestedStartingReplacement: options?.withReplacement }));
    return true;
  };
